import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/feature/PageHero';
import { useSEO } from '../../utils/seo';

const CompanyPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://okayama-pet-funeral.example.com';

  useSEO({
    title: '会社概要｜岡山市のペット葬儀｜おかやまペット葬儀株式会社',
    description: 'おかやまペット葬儀株式会社の会社概要。岡山市北区に所在。2000年設立、従業員9名。ペット火葬・葬儀サービス、納骨・永代供養、仏具販売。営業時間9:00-18:00、定休日日曜・祝日。',
    keywords: '岡山市,ペット葬儀,会社概要,アクセス,所在地,営業時間,おかやまペット葬儀株式会社',
    canonical: `${siteUrl}/company`,
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}/company#organization`,
      name: 'おかやまペット葬儀株式会社',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      foundingDate: '2000',
      founder: {
        '@type': 'Person',
        name: '岡山太郎',
        jobTitle: '代表取締役',
      },
      numberOfEmployees: 9,
      address: {
        '@type': 'PostalAddress',
        streetAddress: '○○町1-2-3',
        addressLocality: '岡山市北区',
        addressRegion: '岡山県',
        postalCode: '700-0000',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 34.6617,
        longitude: 133.935,
      },
      telephone: '+81-86-012-3456',
      faxNumber: '+81-86-012-3457',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
      areaServed: {
        '@type': 'City',
        name: '岡山市',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <PageHero title="会社概要" subtitle="Company Profile" />

      {/* 会社情報セクション */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="order-2 lg:order-1">
              <div className="bg-white rounded-3xl p-8 shadow-lg">
                <h1 className="text-2xl sm:text-3xl font-serif font-bold text-gray-800 mb-8">
                  会社情報
                </h1>
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top w-1/3">
                        会社名
                      </td>
                      <td className="py-4 text-sm text-gray-800">
                        おかやまペット葬儀株式会社
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        代表取締役
                      </td>
                      <td className="py-4 text-sm text-gray-800">岡山 太郎</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        設立
                      </td>
                      <td className="py-4 text-sm text-gray-800">2000年</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        従業員数
                      </td>
                      <td className="py-4 text-sm text-gray-800">9名</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        所在地
                      </td>
                      <td className="py-4 text-sm text-gray-800">
                        〒700-0000
                        <br />
                        岡山市北区○○町1-2-3
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        電話番号
                      </td>
                      <td className="py-4 text-sm text-gray-800">
                        <a
                          href="tel:086-012-3456"
                          className="hover:text-purple-600 transition-colors"
                        >
                          086-012-3456
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        FAX
                      </td>
                      <td className="py-4 text-sm text-gray-800">086-012-3457</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        営業時間
                      </td>
                      <td className="py-4 text-sm text-gray-800">9:00 〜 18:00</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        定休日
                      </td>
                      <td className="py-4 text-sm text-gray-800">日曜日・祝日</td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        事業内容
                      </td>
                      <td className="py-4 text-sm text-gray-800">
                        ペット火葬・葬儀サービス
                        <br />
                        ペット納骨・永代供養
                        <br />
                        ペット仏具販売
                      </td>
                    </tr>
                    <tr>
                      <td className="py-4 pr-6 text-sm font-bold text-purple-600 align-top">
                        加盟団体
                      </td>
                      <td className="py-4 text-sm text-gray-800">
                        日本ペット葬祭協会
                        <br />
                        岡山県ペット葬儀事業者協会
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="rounded-3xl overflow-hidden shadow-lg mb-4">
                <img
                  src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/5bc82b00e6f671cd1275a3112c62d660.jpeg"
                  alt="おかやまペット葬儀 本社外観"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <p className="text-center text-sm text-gray-600">
                おかやまペット葬儀株式会社 本社
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* アクセスマップセクション */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-purple-100 to-pink-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-gray-800 mb-4">
              アクセス
            </h2>
          </div>

          <div className="bg-white rounded-3xl overflow-hidden shadow-lg mb-8">
            <div className="w-full h-96 sm:h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d52634.64267240728!2d133.89234!3d34.6617!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x355408e8b3c2b3e9%3A0x3e3b3e3b3e3b3e3b!2z5bKh5bGx5biC!5e0!3m2!1sja!2sjp!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="おかやまペット葬儀株式会社の地図"
              ></iframe>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-md">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-train-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-3">
                電車でのアクセス
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                JR岡山駅から車で約15分
                <br />
                岡山電車道「○○駅」下車徒歩10分
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-car-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-3">
                車でのアクセス
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                山陽自動車道「岡山IC」から約20分
                <br />
                駐車場完備（10台）
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-md">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <i className="ri-bus-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center mb-3">
                バスでのアクセス
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed text-center">
                岡山駅前バスターミナルから
                <br />
                「○○行き」乗車、「○○町」下車徒歩5分
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CompanyPage;
